import {Component, computed, inject, input, signal, ChangeDetectionStrategy} from "@angular/core"
import {MaterialNode, RgbCurve} from "@cm/material-nodes"
import {Vector2Like} from "@cm/math"
import {RgbCurvesComponent} from "@common/components/curves/rgb-curves/rgb-curves.component"
import {PreviewSceneService} from "@app/material-editor/services/preview-scene.service"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-rgb-curve-widget",
    templateUrl: "./rgb-curve-widget.component.html",
    styleUrls: ["./rgb-curve-widget.component.scss"],
    imports: [RgbCurvesComponent],
})
export class RgbCurveWidgetComponent {
    public readonly $material = input.required<MaterialNode>({alias: "material"})
    readonly previewSceneService = inject(PreviewSceneService)

    readonly $width = computed(() => {
        return 100
    })

    readonly $height = computed(() => {
        return 100
    })

    readonly $rgbCurve = computed(() => {
        const material = this.$material()
        if (material instanceof RgbCurve) return material
        return undefined
    })
    private readonly $triggerRecompute = signal(0)
    readonly $controlPoints = computed(() => {
        this.$triggerRecompute()
        const rgbCurve = this.$rgbCurve()
        if (!rgbCurve) return undefined
        const controlPoints = RgbCurve.controlPointsFromSettings(rgbCurve.parameters.settings)
        return {
            r: controlPoints[0],
            g: controlPoints[1],
            b: controlPoints[2],
            rgb: controlPoints[3],
        }
    })

    // FIXME: this is a quick hack to update the material graph after changing the texture revision.
    triggerGraphUpdate(): void {
        this.previewSceneService.$refresh.next()
    }

    private updateControlPoints(controlPoints: {r: Vector2Like[]; g: Vector2Like[]; b: Vector2Like[]; rgb: Vector2Like[]}) {
        const rgbCurve = this.$rgbCurve()
        if (!rgbCurve) return
        rgbCurve.updateParameters({
            settings: {
                ...rgbCurve.parameters.settings,
                ...RgbCurve.settingsFromControlPoints([controlPoints.r, controlPoints.g, controlPoints.b, controlPoints.rgb], rgbCurve.parameters.settings),
            },
        })

        this.$triggerRecompute.update((x) => x + 1)

        this.triggerGraphUpdate()
    }

    addControlPoint(curveType: "rgb" | "r" | "g" | "b", point: Vector2Like) {
        const rgbCurve = this.$rgbCurve()
        const controlPoints = this.$controlPoints()
        if (!rgbCurve || !controlPoints) return

        this.updateControlPoints({
            ...controlPoints,
            [curveType]: [...controlPoints[curveType], point],
        })
    }

    removeControlPoint(curveType: "rgb" | "r" | "g" | "b", pointId: number) {
        const rgbCurve = this.$rgbCurve()
        const controlPoints = this.$controlPoints()
        if (!rgbCurve || !controlPoints) return

        this.updateControlPoints({
            ...controlPoints,
            [curveType]: controlPoints[curveType].filter((_, i) => i !== pointId),
        })
    }

    changeControlPoint(curveType: "rgb" | "r" | "g" | "b", pointId: number, point: Vector2Like) {
        const rgbCurve = this.$rgbCurve()
        const controlPoints = this.$controlPoints()
        if (!rgbCurve || !controlPoints) return

        this.updateControlPoints({
            ...controlPoints,
            [curveType]: controlPoints[curveType].map((p, i) => (i === pointId ? point : p)),
        })
    }
}
